/**
 * App-Sidebar mit hierarchischer Information-Architecture.
 *
 * Drei Render-Ebenen:
 *   1. Section (top-level, Emoji + Label)
 *   2. Subgroup (innerhalb einer Section)
 *   3. Leaf-Item (Link oder Stub)
 *
 * Stub-Items (kein `to`) werden grau dargestellt + nicht klickbar — die
 * Sidebar zeigt damit das gesamte Geruest, auch wenn ein Modul noch nicht
 * implementiert ist. Sobald eine Phase liefert, ergaenzt man in
 * `lib/navigation.ts` nur das `to`-Feld.
 *
 * Open/Closed-Zustand der Sections wird in localStorage persistiert. Beim
 * Erst-Boot ist genau die Section offen, in der der aktuelle Pfad liegt
 * — sonst alle zu (sonst wuerde 11 Sections × ~5 Items eine sehr lange
 * Liste).
 */
import { useEffect, useState } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import {
  NAV_SECTIONS,
  isAnyChildActive,
  isItemActive,
  type NavChild,
  type NavLeaf,
  type NavSection,
} from '../../lib/navigation';

const STORAGE_KEY = 'busch-erp.sidebar.openSections.v1';

function loadOpenState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function saveOpenState(state: Record<string, boolean>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage-Quota oder Privacy-Mode — ignorieren, ist nur UX.
  }
}

export interface SidebarProps {
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const router = useRouter();
  const pathname = router.state.location.pathname;

  // Init: gespeicherte Werte + Auto-Open der Section, in der der aktive Pfad liegt.
  const [openMap, setOpenMap] = useState<Record<string, boolean>>(() => {
    const stored = loadOpenState();
    const result = { ...stored };
    for (const section of NAV_SECTIONS) {
      if (section.children && isAnyChildActive(section.children, pathname)) {
        result[section.id] = true;
      }
    }
    return result;
  });

  useEffect(() => {
    saveOpenState(openMap);
  }, [openMap]);

  function toggleSection(id: string) {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <nav className="flex h-full flex-col gap-1 overflow-y-auto p-3 text-sm">
      <Link to="/" onClick={onNavigate} className="mb-2 px-2 py-2 font-semibold text-slate-900">
        busch-erp
      </Link>
      <div className="flex flex-col gap-px">
        {NAV_SECTIONS.map((section) => (
          <SidebarSection
            key={section.id}
            section={section}
            pathname={pathname}
            open={openMap[section.id] ?? false}
            onToggle={() => toggleSection(section.id)}
            onNavigate={onNavigate ?? (() => undefined)}
          />
        ))}
      </div>
    </nav>
  );
}

function SidebarSection({
  section,
  pathname,
  open,
  onToggle,
  onNavigate,
}: {
  section: NavSection;
  pathname: string;
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const isDirectLink = section.to !== undefined;
  const directActive =
    isDirectLink &&
    (pathname === section.to || (section.to !== '/' && pathname.startsWith(section.to + '/')));
  const childActive = section.children ? isAnyChildActive(section.children, pathname) : false;

  if (isDirectLink && section.to) {
    return (
      <Link
        to={section.to}
        onClick={onNavigate}
        className={`block rounded-md px-2 py-1.5 ${
          directActive
            ? 'bg-brand-50 text-brand-700 font-medium'
            : 'text-slate-700 hover:bg-slate-100'
        }`}
      >
        {section.label}
      </Link>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left ${
          childActive ? 'text-brand-700 font-medium' : 'text-slate-700 hover:bg-slate-100'
        }`}
        aria-expanded={open}
      >
        <span className="flex-1">{section.label}</span>
        <span className={`text-xs text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}>
          ▶
        </span>
      </button>
      {open && section.children && (
        <div className="mt-1 ml-4 border-l border-slate-200 pl-2">
          {section.children.map((child, idx) => (
            <SidebarChild
              key={`${section.id}-${idx}`}
              child={child}
              pathname={pathname}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarChild({
  child,
  pathname,
  onNavigate,
}: {
  child: NavChild;
  pathname: string;
  onNavigate: () => void;
}) {
  if (child.kind === 'item') {
    return <SidebarLeaf item={child} pathname={pathname} onNavigate={onNavigate} />;
  }
  // Subgroup — Header (klein, all-caps) + Items darunter, kein collapse fuer V1.
  return (
    <div className="mt-2 first:mt-0">
      <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {child.label}
      </div>
      {child.items.map((item, idx) => (
        <SidebarLeaf
          key={`${child.label}-${idx}`}
          item={item}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

function SidebarLeaf({
  item,
  pathname,
  onNavigate,
}: {
  item: NavLeaf;
  pathname: string;
  onNavigate: () => void;
}) {
  const active = isItemActive(item, pathname);
  if (!item.to) {
    // Stub — grau, nicht klickbar, Hint als Tooltip.
    return (
      <div
        className="px-2 py-1 text-slate-400 cursor-not-allowed select-none"
        title={item.hint ?? 'kommt mit der jeweiligen Phase'}
      >
        {item.label}
      </div>
    );
  }
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={`block rounded-md px-2 py-1 ${
        active
          ? 'bg-brand-50 text-brand-700 font-medium border-l-2 border-brand-600 pl-[6px]'
          : 'text-slate-600 hover:bg-slate-100'
      }`}
    >
      {item.label}
    </Link>
  );
}

/** Mobile-Burger fuer Top-Bar. */
export function SidebarToggle({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="lg:hidden inline-flex items-center justify-center rounded-md p-1.5 text-slate-700 hover:bg-slate-100"
      aria-label="Menü öffnen"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>
  );
}

/** Wrapper, der die Sidebar als slide-in-Drawer auf Mobile rendert. */
export function SidebarShell({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Body-Scroll-Lock waehrend Drawer offen ist (mobile UX).
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  return (
    <>
      {/* Desktop-fixed-Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-slate-200 bg-white">
        <div className="flex w-full flex-col">
          <Sidebar />
        </div>
      </aside>
      {/* Mobile-Drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden />
          <aside className="relative z-50 flex w-72 flex-col bg-white shadow-xl">
            <Sidebar onNavigate={onClose} />
          </aside>
        </div>
      )}
    </>
  );
}
